import { Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';

import { MessageModule } from 'primeng/message';
import { VButtonV2Component } from 'src/app/core/components/ui-kit/buttons/button-v2/button.component';
import { VMessageComponent } from 'src/app/core/components/ui-kit/message/message.component';
import { VUserCardComponent } from 'src/app/core/components/ui-kit/user-card/user-card.component';
import { SharedModule } from 'src/app/core/shared.module';
import { toUserSummary, UserSummary } from 'src/app/models/user';
import {
  Invitation,
  KratosAuthenticationService,
} from 'src/app/services/authentication/kratos.service';
import { ResetOnboardingAction } from 'src/app/store/onboarding/onboarding.actions';
import { LoadOrganizationAction } from 'src/app/store/organization/organization.actions';
import { OrganizationStateModule } from 'src/app/store/organization/organization.state.module';
import {
  LoadUserAction,
  LogoutAction,
  SelectOrganizationAction,
} from 'src/app/store/user/user.actions';
import { UserState } from 'src/app/store/user/user.state';
import { VCardComponent } from '../../../core/components/ui-kit/card/card.component';

interface VM {
  isLoading: boolean;
  user?: UserSummary;
  isAlreadyMember?: boolean;
}

const JOIN_ORGANIZATION_MUTATION = gql`
  mutation JoinOrganization($token: String!) {
    joinOrganization(token: $token) {
      id
    }
  }
`;

@Component({
  selector: 'app-page-join-organization',
  imports: [
    SharedModule,
    VCardComponent,
    VButtonV2Component,
    MessageModule,
    VUserCardComponent,
    VMessageComponent,
    ReactiveFormsModule,
    OrganizationStateModule,
  ],
  templateUrl: './join-organization.component.html',
})
export class JoinOrganizationPageComponent {
  private auth = inject(KratosAuthenticationService);
  private apollo = inject(Apollo);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);

  invitation?: Invitation;
  isJoining = false;

  constructor() {
    this.route.queryParams.subscribe(params => {
      this.loadInvitation(params['token']);
    });
  }

  vm$: Observable<VM> = this.store.select(UserState.getUser).pipe(
    map(user => {
      if (!user) {
        return {
          isLoading: true,
        };
      }

      return {
        isLoading: false,
        user: toUserSummary(user),
        isAlreadyMember:
          user.memberships.find(membership => {
            return (
              membership.organizationId === this.invitation?.organization.id
            );
          }) !== undefined,
      };
    })
  );

  joinOrgForm = new FormGroup({});
  joinOrganizationError?: string;

  loadInvitation(token: string) {
    this.auth.getInvitation(token).subscribe({
      next: invitation => {
        this.invitation = invitation;
      },
    });

    this.store.dispatch(new LoadUserAction());
  }

  onFormSubmit() {
    if (!this.joinOrgForm.valid) {
      return;
    }

    this.joinOrganizationError = undefined;
    this.joinOrgForm.disable();

    this.isJoining = true;

    this.apollo
      .mutate({
        mutation: JOIN_ORGANIZATION_MUTATION,
        variables: {
          token: this.invitation?.token,
        },
      })
      .subscribe({
        next: () => {
          this.store
            .dispatch([
              new SelectOrganizationAction(this.invitation!.organization.id!),
              new LoadUserAction(),
              new LoadOrganizationAction(),
              new ResetOnboardingAction(),
            ])
            .subscribe({
              next: () => {
                this.router.navigate(['/']);
              },
            });
        },
        error: err => {
          this.isJoining = false;
          this.joinOrganizationError = err.toString();
          this.joinOrgForm.enable();
        },
      });
  }

  goToDashboard() {
    this.store
      .dispatch(new SelectOrganizationAction(this.invitation!.organization.id))
      .subscribe({
        next: () => {
          this.router.navigate(['/']);
        },
      });
  }

  logout() {
    this.store.dispatch(
      new LogoutAction('/register', {
        token: this.invitation?.token,
      })
    );
  }
}
